/**
 * The caller's plan id, for the surfaces that PACKAGE a feature rather than
 * bill for it.
 *
 * Shares `useCurrentSubscription`'s query key on purpose, so the billing page
 * and the transport ceiling are one cached read rather than two calls for the
 * same row.
 *
 * Every failure mode answers `null`, and `null` means "unrestricted" to every
 * consumer (see `transportCapabilitiesForPlan`). That direction is deliberate:
 * a subscriptions API that is slow, unreachable, or has no row for this customer
 * must not silently strip capabilities from a paying tenant — a packaging
 * decision the client cannot verify is one it should not enforce. The backend
 * gates the entitlements that actually matter.
 *
 * A subscription that is cancelled/expired resolves to the free tier rather than
 * to `null`: there the plan IS known, and it is the one the customer is on.
 */
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@authe/ui-core/api";
import * as React from "react";
import { bundledPlans, resolvePlanById, type Plan } from "@authe/fe-commons/plans";
import {
  createApiSubscriptionsSource,
  isEntitledStatus,
  type SubscriptionSummary,
} from "@/api/subscriptionsSource";

/** The shared cache key for "the caller's current subscription". */
export const CURRENT_SUBSCRIPTION_QUERY_KEY = ["subscriptions", "current"] as const;

/** Plan id a subscription grants right now, or `null` when unknown. */
export function planIdFor(subscription: SubscriptionSummary | null | undefined): string | null {
  if (!subscription) return null;
  return isEntitledStatus(subscription.status) ? subscription.planId : "free";
}

/** Plan ids this build knows how to reason about. */
const KNOWN_PLAN_IDS: ReadonlySet<string> = new Set(bundledPlans().map((plan) => plan.id));

/** Stable identity, so the provider's memo does not churn on every render. */
const EMPTY_OVERRIDES: Record<string, unknown> = {};

/**
 * The resolved plan, or `null` when it is not known.
 *
 * `null` covers three distinct situations that must behave identically — no
 * subscription row, a read that has not landed or failed, and a plan id this
 * build does not recognise. The last is the subtle one: a bespoke enterprise
 * tier minted server-side would otherwise fall through `resolvePlanById` to
 * FREE and strip the paid features of the customer paying the most, which is
 * the exact inversion of their contract.
 *
 * Deliberately NOT `resolvePlanById` alone. That one is right for the Billing
 * page — there "no row" really does mean the free tier, and it is being
 * DISPLAYED rather than enforced — and wrong for anything that hides a control.
 */
export function planFor(subscription: SubscriptionSummary | null | undefined): Plan | null {
  const id = planIdFor(subscription);
  if (!id || !KNOWN_PLAN_IDS.has(id)) return null;
  return resolvePlanById(id);
}

function useCurrentSubscriptionRow(): SubscriptionSummary | null | undefined {
  const client = useApiClient();
  const source = React.useMemo(() => createApiSubscriptionsSource(client), [client]);

  const query = useQuery({
    queryKey: CURRENT_SUBSCRIPTION_QUERY_KEY,
    queryFn: () => source.getCurrentSubscription(),
    // A plan does not change mid-session, and this read gates nothing the
    // backend is not already gating — so it is worth exactly one call.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  return query.data;
}

export function useCurrentPlanId(): string | null {
  return planIdFor(useCurrentSubscriptionRow());
}

/**
 * The plan and the subscription's entitlement overrides, for `<PlanProvider>`.
 *
 * The overrides go over RAW: `grantedEntitlements` layers them, and it is the
 * mirror of the Python the authorizer runs. Resolving them here too would be a
 * second implementation of the one piece of arithmetic that decides what a
 * customer bought, and the two would disagree about a withdrawn feature long
 * before anyone noticed.
 */
export function useCurrentPlan(): {
  plan: Plan | null;
  overrides: Record<string, unknown>;
  metadata: Record<string, unknown>;
} {
  const subscription = useCurrentSubscriptionRow();
  return {
    plan: planFor(subscription),
    overrides: subscription?.entitlementOverrides ?? EMPTY_OVERRIDES,
    // The row's own bag, which can carry a per-customer `load_profile`. A
    // bespoke contract ("this customer runs enormous investigations and we
    // agreed to carry it") is expressed here rather than by minting a plan for
    // one customer.
    metadata: subscription?.metadata ?? EMPTY_OVERRIDES,
  };
}
